import { cva, type VariantProps } from "class-variance-authority";

/**
 * Input shell — wraps `<input>` (and optional prefix/suffix slots) so the
 * border/focus ring/disabled background lives on the wrapper, not the input.
 * The inner input is unstyled so prefix icons can sit flush.
 *
 * Spec: docs/design-system/03-primitives.md → Input section.
 */
export const inputShell = cva(
  "flex items-center w-full bg-transparent transition-colors " +
    "border rounded-[8px] " +
    "focus-within:border-[var(--ring)] focus-within:shadow-[var(--shadow-focus-ring)] " +
    "[&:has(input:disabled)]:opacity-60 [&:has(input:disabled)]:cursor-not-allowed [&:has(input:disabled)]:bg-[var(--muted)]",
  {
    variants: {
      size: {
        sm: "h-8 text-xs",
        md: "h-9 text-sm",
        lg: "h-11 text-sm",
      },
      invalid: {
        true: "border-[var(--destructive)] focus-within:border-[var(--destructive)]",
        false: "border-[var(--border)]",
      },
    },
    defaultVariants: { size: "md", invalid: false },
  }
);

export type InputShellVariantProps = VariantProps<typeof inputShell>;

/**
 * Padding for the inner `<input>`. Side-padding is split into pl-/pr- (NOT px-)
 * because tailwind-merge would otherwise collapse a `pl-2 px-3.5` combo into
 * `px-3.5` and erase the prefix-icon gap.
 *
 * Visible whitespace target ≈ 12–14 px on the bare side, ≈ 8 px on the
 * icon-adjacent side (icon slot already adds 12 px of its own padding).
 */
export function inputInnerPadding({
  hasPrefix,
  hasSuffix,
  size,
}: {
  hasPrefix: boolean;
  hasSuffix: boolean;
  size: "sm" | "md" | "lg";
}): string {
  const basePl = size === "lg" ? "pl-4" : size === "sm" ? "pl-3" : "pl-3.5";
  const basePr = size === "lg" ? "pr-4" : size === "sm" ? "pr-3" : "pr-3.5";
  const left = hasPrefix ? "pl-2" : basePl;
  const right = hasSuffix ? "pr-2" : basePr;
  return `flex-1 h-full bg-transparent outline-none text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] ${left} ${right}`;
}

export const inputSlotIcon =
  "flex items-center justify-center text-[var(--muted-foreground)] px-3";
